import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { commerceConfig } from "../config/commerce";

export interface WishlistItem {
  id: string | number;
  handle: string;
  title: string;
  price: number;
  compareAt?: number | null;
  image: string;
  image2?: string;
  category?: string;
  tag?: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  has: (id: string | number) => boolean;
  toggle: (product: WishlistItem) => void;
  remove: (id: string | number) => void;
  count: number;
}

const WishlistContext = createContext<WishlistContextType | null>(null);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<WishlistItem[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      return JSON.parse(localStorage.getItem("meoora_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  // Sync to localStorage and database
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("meoora_wishlist", JSON.stringify(items));

      // If user is logged in, sync wishlist to DB
      const token = localStorage.getItem("meoora_auth_token");
      if (token) {
        const timeout = setTimeout(() => {
          fetch(`${commerceConfig.apiUrl}/user/sync`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ wishlist: items }),
          }).catch(() => {
            // Ignore background sync errors
          });
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [items]);

  // Listen for external updates (e.g. login/signup guest merge)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorageUpdate = () => {
      try {
        const updated = JSON.parse(localStorage.getItem("meoora_wishlist") || "[]");
        setItems(updated);
      } catch {
        // Ignore
      }
    };
    window.addEventListener("meoora_wishlist_updated", handleStorageUpdate);
    return () => window.removeEventListener("meoora_wishlist_updated", handleStorageUpdate);
  }, []);

  const has = (id: string | number) => items.some((i) => String(i.id) === String(id));

  const toggle = (product: WishlistItem) => {
    setItems((prev) =>
      prev.some((i) => String(i.id) === String(product.id))
        ? prev.filter((i) => String(i.id) !== String(product.id))
        : [
            ...prev,
            {
              id: product.id,
              handle: product.handle,
              title: product.title,
              price: product.price,
              compareAt: product.compareAt,
              image: product.image,
              image2: product.image2,
              category: product.category,
              tag: product.tag,
            },
          ]
    );
  };

  const remove = (id: string | number) => {
    setItems((prev) => prev.filter((i) => String(i.id) !== String(id)));
  };

  const count = useMemo(() => items.length, [items]);

  return (
    <WishlistContext.Provider value={{ items, has, toggle, remove, count }}>
      {children}
    </WishlistContext.Provider>
  );
};

